/**
 * PublicMenuDialogs — all modal dialogs and footer for PublicMenuPage.
 * Covers: upsell suggestions, checkout, allergen info, footer, reservation, auth, tracking,
 *         table session modals, PWA prompt, AI chat, bill requested screen.
 */
import { useTranslation } from "react-i18next";
import { toBaseLanguage } from "@/i18n";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Plus, AlertTriangle } from "lucide-react";
import { Toaster } from "sonner";
import CarolWidget from "@/components/public/CarolWidget";
import { AllergenInfoDialog } from "@/components/public/MenuComponents";
import { PWAInstallPrompt } from "@/components/public/PWAInstallPrompt";
import { CustomerAuthModal } from "@/components/public/CustomerAuth";
import { OrderTrackingModal } from "@/components/public/OrderTracking";
import { TableSessionModal } from "@/components/public/TableSessionModal";
import { TableSessionOrdersModal } from "@/components/public/TableSessionOrdersModal";
import { BillRequestedScreen } from "@/components/public/BillRequestedScreen";
import CheckoutDialog from "@/components/public/CheckoutDialog";
import ReservationDialog from "@/components/public/ReservationDialog";
import { resolveMedia } from "@/utils/mediaUtils";
import { trackPixelEvent } from "@/hooks/useAnalytics";
import { trackAddToCart } from "@/utils/tracking";
import { getAvailableDates as getPolicyDates, getTimeSlots as getPolicyTimeSlots } from "@/utils/scheduleUtils";
const DEFAULT_LOGO = "";
const DEFAULT_WHATSAPP = "";
export function PublicMenuDialogs({
  // Suggestions
  showSuggestions, setShowSuggestions, suggestions, addSuggestionToCart, trackUpsell,
  categories, tc, fmt,
  // Checkout
  showCheckout, setShowCheckout, restaurant, restaurantId, isTableOrder, isOpen,
  tableName, tableNumber, tablePayAtEnd, tablePaymentEnabled, paymentAtTableEnabled,
  cart, cartTotal, smartPricing, checkoutStep, setCheckoutStep, orderType, setOrderType,
  deliveryAddress, setDeliveryAddress, deliveryLat, setDeliveryLat, deliveryLng, setDeliveryLng,
  addressNumber, setAddressNumber, addressComplement, setAddressComplement, addressBarrio, setAddressBarrio,
  fullAddress,
  customerName, setCustomerName, customerPhone, setCustomerPhone, orderNotes, setOrderNotes,
  couponCode, setCouponCode, referralDiscount, setReferralDiscount, loyaltyReward, setLoyaltyReward,
  paymentMethod, setPaymentMethod, changeFor, setChangeFor,
  scheduleDate, setScheduleDate, scheduleTime, setScheduleTime, schedulingMode,
  getAvailableDates, getTimeSlots,
  checkoutSuggestions, setCheckoutSuggestions, addToCartSilent,
  submitting, submitOrder,
  // Allergen + footer
  showAllergenInfo, setShowAllergenInfo, allAllergens, logoUrl, logoClass, isDark,
  // Reservation
  showReservation, setShowReservation, reservationData, setReservationData,
  reservationConfirmed, setReservationConfirmed, reservationSubmitting, submitReservation,
  reservationSlotAlternatives, selectReservationAlternative,
  // Auth
  showAuthModal, setShowAuthModal, customerSession, handleCustomerLogin, handleCustomerLogout,
  // Tracking
  showTrackingModal, setShowTrackingModal, trackingOrderId,
  // Table session
  showTableSessionModal, setShowTableSessionModal, tableSessionLoading, startTableSession, handleViewOnly,
  showTableOrdersModal, setShowTableOrdersModal, tableSession, requestBill, requestingBill,
  billRequested, setBillRequested, setTableSession, tableId,
  // Cart helpers
  setCart, updateQuantity,
}) {
  const { t, i18n } = useTranslation();
  const baseLang = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const dateLocale = baseLang === "pt" ? "pt-PT" : baseLang === "es" ? "es-ES" : "en-US";
  const availableDates = getPolicyDates(
    restaurant,
    restaurant?.scheduling_config || {},
    new Date(),
    "reservation",
  ).map(d => ({
    ...d,
    label: new Date(`${d.value}T12:00:00`).toLocaleDateString(
      dateLocale,
      { weekday: "short", day: "numeric", month: "short" },
    ),
  }));
  const getReservationTimeSlots = (date) => getPolicyTimeSlots(
    restaurant,
    restaurant?.scheduling_config || {},
    date,
    new Date(),
    "reservation",
  );
  return (
    <>
      {/* Upsell Suggestions Dialog */}
      <Dialog open={showSuggestions} onOpenChange={setShowSuggestions}>
        <DialogContent className="max-w-sm p-0 overflow-hidden border-0 shadow-2xl" data-testid="suggestions-dialog">
          <div className="px-5 pt-5 pb-3 relative" style={{ background: `linear-gradient(135deg, ${tc.primary}, ${tc.primary}dd)` }}>
            <div className="absolute top-0 right-0 w-24 h-24 rounded-full opacity-10 bg-white -mr-6 -mt-6" />
            <p className="text-xs font-bold uppercase tracking-widest text-white/80">{t("publicMenu.suggestions.recommendedForYou")}</p>
            <p className="text-lg font-bold text-white mt-1">{t("publicMenu.suggestions.completeOrder")}</p>
            <p className="text-xs text-white/60">{t("publicMenu.suggestions.othersAlsoOrdered")}</p>
          </div>
          <div className="px-4 pb-3 space-y-2 -mt-2">
            {suggestions.map(s => {
              const catName = categories.find(c => c.id === s.category_id)?.name || "";
              return (
                <button key={s.id} onClick={() => addSuggestionToCart(s)} className="w-full flex items-center gap-3 p-3 rounded-xl border-2 border-transparent hover:border-current bg-white shadow-sm hover:shadow-md transition-all group" data-testid={`suggestion-${s.id}`}>
                  {s.image_url ? <img src={resolveMedia(s.image_url)} alt={s.name} className="w-16 h-16 rounded-xl object-cover shrink-0 shadow-sm" />
                    : <div className="w-16 h-16 rounded-xl shrink-0 flex items-center justify-center text-2xl" style={{ backgroundColor: `${tc.primary}15` }}>
                        {catName?.toLowerCase().includes("bebida") ? "🍺" : catName?.toLowerCase().includes("postre") ? "🍰" : catName?.toLowerCase().includes("entrada") ? "🥗" : "🍽️"}
                      </div>}
                  <div className="flex-1 min-w-0 text-left">
                    {catName && <span className="text-[10px] font-semibold uppercase tracking-wider block mb-0.5" style={{ color: tc.primary }}>{catName}</span>}
                    <span className="font-bold text-sm text-gray-900 block truncate">{s.name}</span>
                    <span className="font-extrabold text-base" style={{ color: tc.primary }}>+{fmt(s.price)}</span>
                  </div>
                  <div className="shrink-0 w-10 h-10 rounded-full text-white flex items-center justify-center shadow-md group-hover:scale-110 transition-transform" style={{ backgroundColor: tc.primary }}><Plus className="w-5 h-5" /></div>
                </button>
              );
            })}
          </div>
          <div className="px-4 pb-4">
            <button onClick={() => { suggestions.forEach(s => trackUpsell("skipped", "popup", s)); setShowSuggestions(false); }} className="w-full text-center text-xs text-gray-400 hover:text-gray-600 py-2 transition" data-testid="skip-suggestions">{t("publicMenu.suggestions.skip")}</button>
          </div>
        </DialogContent>
      </Dialog>
      <CheckoutDialog
        open={showCheckout}
        onClose={() => { setShowCheckout(false); setCheckoutStep(isTableOrder ? 2 : 1); }}
        restaurant={restaurant}
        tc={tc}
        fmt={fmt}
        restaurantId={restaurantId}
        isTableOrder={isTableOrder}
        isOpen={isOpen}
        table={{ name: tableName, number: tableNumber, payAtEnd: tablePayAtEnd, paymentEnabled: tablePaymentEnabled, paymentAtTableEnabled: paymentAtTableEnabled !== false }}
        cart={cart}
        cartTotal={cartTotal}
        smartPricing={smartPricing}
        categories={categories}
        onUpdateQuantity={updateQuantity}
        step={checkoutStep}
        onStepChange={setCheckoutStep}
        orderType={orderType}
        onOrderTypeChange={setOrderType}
        delivery={{ address: deliveryAddress, lat: deliveryLat, lng: deliveryLng, number: addressNumber, complement: addressComplement, barrio: addressBarrio, full: fullAddress }}
        onDeliveryChange={{ address: setDeliveryAddress, lat: setDeliveryLat, lng: setDeliveryLng, number: setAddressNumber, complement: setAddressComplement, barrio: setAddressBarrio }}
        customer={{ name: customerName, phone: customerPhone, notes: orderNotes }}
        onCustomerChange={{ name: setCustomerName, phone: setCustomerPhone, notes: setOrderNotes }}
        coupon={{ code: couponCode, discount: referralDiscount }}
        onCouponChange={{ code: setCouponCode, discount: setReferralDiscount }}
        loyalty={{ reward: loyaltyReward }}
        onLoyaltyChange={setLoyaltyReward}
        payment={{ method: paymentMethod, changeFor }}
        onPaymentChange={{ method: setPaymentMethod, changeFor: setChangeFor }}
        schedule={{ date: scheduleDate, time: scheduleTime, mode: schedulingMode }}
        onScheduleChange={{ date: setScheduleDate, time: setScheduleTime }}
        getAvailableDates={getAvailableDates}
        getTimeSlots={getTimeSlots}
        suggestions={checkoutSuggestions}
        onAddSuggestion={addToCartSilent}
        onRemoveSuggestion={(id) => setCheckoutSuggestions(prev => prev.filter(x => x.id !== id))}
        onTrackUpsell={trackUpsell}
        submitting={submitting}
        onSubmit={submitOrder}
      />
      <AllergenInfoDialog open={showAllergenInfo} onOpenChange={setShowAllergenInfo} />

      {/* Footer */}
      <footer className="mt-16 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #0a0a0a 0%, #111111 100%)' }}>
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div className="max-w-5xl mx-auto px-6 pt-12 pb-8 relative z-10">
          <div className="flex flex-col items-center gap-6">
            <div className="flex flex-col items-center gap-3">
              {logoUrl && <img src={logoUrl} alt="Logo" className={`w-16 h-16 rounded-xl shadow-lg shadow-black/40 ${logoClass}`} data-testid="footer-logo" onError={(e) => { e.target.src = DEFAULT_LOGO; }} />}
              <h3 className="text-xl sm:text-2xl font-bold tracking-[0.2em] text-white/90" style={{ fontFamily: "'Georgia', serif" }}>{restaurant?.name || ""}</h3>
              <div className="w-16 h-[1px] mt-1" style={{ background: `linear-gradient(90deg, transparent, ${tc.primary}, transparent)` }} />
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {restaurant?.phone && <a href={`https://wa.me/${restaurant.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-white/[0.07] backdrop-blur-sm border border-white/10 text-white/80 hover:bg-white/[0.12] hover:text-white transition-all duration-300 text-sm font-medium tracking-wide" data-testid="whatsapp-footer-link">
                <svg viewBox="0 0 24 24" className="w-4 h-4 text-green-400" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                {restaurant.phone.replace(/^\+?34/, "").replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3")}
              </a>}
              {restaurant?.instagram_url && (
                <a href={restaurant.instagram_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-white/[0.07] backdrop-blur-sm border border-white/10 text-white/80 hover:bg-white/[0.12] hover:text-white transition-all duration-300 text-sm font-medium tracking-wide" data-testid="instagram-footer-link">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 text-pink-400" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  <span>Instagram</span>
                </a>
              )}
            </div>
            {restaurant?.cif && <p className="text-[11px] text-white/25 tracking-widest uppercase">{restaurant.cif}</p>}
            {allAllergens.length > 0 && (
              <button onClick={() => setShowAllergenInfo(true)} className="text-[11px] text-amber-400/60 hover:text-amber-400 transition flex items-center gap-1.5 tracking-wide"><AlertTriangle className="w-3 h-3" /> {t("publicMenu.allergenInfo")}</button>
            )}
          </div>
          <div className="mt-8 pt-6 border-t border-white/[0.08]">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-4">
                <a href="#" className="text-[10px] text-white/70 hover:text-white transition tracking-wide">{t("publicMenu.footer.privacy")}</a>
                <span className="text-white/40">|</span>
                <a href="#" className="text-[10px] text-white/70 hover:text-white transition tracking-wide">{t("publicMenu.footer.terms")}</a>
              </div>
              <p className="text-[10px] text-white/70 tracking-wide">{t("publicMenu.footer.vat")}</p>
            </div>
            <p className="text-[10px] text-white/50 mt-4 text-center tracking-widest">Powered by <a href="/" target="_blank" rel="noopener noreferrer" className="hover:text-white/80 transition font-medium" style={{ color: tc.primary }} data-testid="kaiso-footer-link">KAISO SYSTEM</a></p>
          </div>
        </div>
      </footer>

      <ReservationDialog
        open={showReservation}
        onClose={() => { setShowReservation(false); }}
        restaurant={restaurant}
        tc={tc}
        data={reservationData}
        onDataChange={setReservationData}
        confirmed={reservationConfirmed}
        submitting={reservationSubmitting}
        onSubmit={submitReservation}
        availableDates={availableDates}
        getAvailableTimeSlots={getReservationTimeSlots}
        slotAlternatives={reservationSlotAlternatives}
        onSelectAlternative={selectReservationAlternative}
      />

      <Toaster position="top-center" />

      <CarolWidget
        restaurantId={restaurantId}
        restaurantName={restaurant?.name}
        lang={toBaseLanguage(restaurant?.locale || "es")}
        botPhotoUrl={restaurant?.carol_photo_url || restaurant?.theme?.logo_url || restaurant?.logo_url || null}
        accentColor={tc.primary}
        cartItems={cart}
        onAddToCart={(item) => {
          const product = { id: item.id, name: item.name, price: item.price, cartId: item.id };
          setCart(prev => {
            const existing = prev.find(i => i.cartId === product.cartId);
            if (existing) return prev.map(i => i.cartId === product.cartId ? { ...i, quantity: i.quantity + 1 } : i);
            return [...prev, { ...product, quantity: 1, originalPrice: item.price }];
          });
          const eventId = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `atc_${Date.now()}`;
          trackPixelEvent("AddToCart", { content_ids: [item.id], content_name: item.name, value: item.price, currency: restaurant?.currency || "EUR" }, { eventID: eventId });
          trackAddToCart({ product_id: item.id, cart_value: item.price, metadata: { product_name: item.name, event_id: eventId, source: "carol_widget" } });
        }}
        onRemoveFromCart={(productId) => {
          setCart(prev => prev.filter(i => i.cartId !== productId && i.id !== productId));
        }}
      />

      <PWAInstallPrompt restaurantName={restaurant?.name} themeColor={tc.primary} />

      <CustomerAuthModal
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        restaurantId={restaurantId}
        restaurantName={restaurant?.name}
        themeColor={tc.primary}
        onLogin={handleCustomerLogin}
        onLogout={handleCustomerLogout}
        customerSession={customerSession}
      />

      <OrderTrackingModal
        open={showTrackingModal}
        onClose={() => setShowTrackingModal(false)}
        orderId={trackingOrderId}
        themeColor={tc.primary}
      />

      <TableSessionModal
        open={showTableSessionModal}
        onOpenChange={setShowTableSessionModal}
        tableName={tableName}
        tableNumber={tableNumber}
        onStartSession={startTableSession}
        onViewOnly={handleViewOnly}
        isLoading={tableSessionLoading}
        themeColor={tc.primary}
      />

      <TableSessionOrdersModal
        open={showTableOrdersModal}
        onOpenChange={setShowTableOrdersModal}
        session={tableSession}
        onRequestBill={requestBill}
        isRequesting={requestingBill}
        themeColor={tc.primary}
      />

      {billRequested && tableSession && (
        <BillRequestedScreen
          session={tableSession}
          total={tableSession.total || 0}
          onClose={() => {
            setBillRequested(false);
            localStorage.removeItem(`kaiso_table_session_${tableId}`);
            setTableSession(null);
          }}
          themeColor={tc.primary}
        />
      )}
    </>
  );
}
